import { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { Upload, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, FileBox, RefreshCw, Eye, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';
import StlViewer from '../components/StlViewer';

const FOOT_LABEL = { left: '왼발', right: '오른발' };

function StlModal({ stl, onSave, onClose }) {
  const [form, setForm] = useState({
    name: stl?.name || '',
    description: stl?.description || '',
    footSide: stl?.footSide || 'left',
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const handleSave = async () => {
    setLoading(true);
    try {
      if (stl?.id) {
        // 파일 교체
        if (file) {
          const fd = new FormData();
          fd.append('stlFile', file);
          await api.post(`/stl/${stl.id}/replace`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        await api.put(`/stl/${stl.id}`, form);
      } else {
        if (!file) return alert('STL 파일을 선택해주세요.');
        const fd = new FormData();
        fd.append('stlFile', file);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        await api.post('/stl', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onSave();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg">
        <h2 className="text-white font-bold mb-4">{stl?.id ? '디자인 수정' : '디자인 등록'}</h2>
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-xs mb-1 block">디자인 이름</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="해모수"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">발 구분</label>
            <div className="flex gap-2">
              {[{ value: 'left', label: '왼발' }, { value: 'right', label: '오른발' }].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setForm(f => ({ ...f, footSide: opt.value }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.footSide === opt.value ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">설명 (선택)</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand resize-none"
            />
          </div>

          {/* 파일 업로드 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">
              {stl?.id ? 'STL 파일 교체 (선택)' : 'STL 파일 *'}
            </label>
            <div
              onClick={() => fileRef.current.click()}
              className="border-2 border-dashed border-gray-700 hover:border-brand rounded-xl p-4 text-center cursor-pointer transition-colors"
            >
              {file ? (
                <p className="text-brand text-sm">{file.name}</p>
              ) : (
                <>
                  <Upload size={20} className="text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">클릭하여 STL 파일 선택</p>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept=".stl" className="hidden" onChange={e => setFile(e.target.files[0])} />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm">취소</button>
          <button onClick={handleSave} disabled={loading} className="flex-1 bg-brand hover:bg-brand-600 disabled:opacity-50 text-white rounded-lg py-2 text-sm font-medium">
            {loading ? '처리 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StlPage() {
  const [list, setList] = useState([]);
  const [modal, setModal] = useState(null);
  const [preview, setPreview] = useState(null); // 3D 프리뷰할 STL 아이템

  const load = () => api.get('/stl').then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const del = async id => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/stl/${id}`);
    load();
  };

  const toggleActive = async item => {
    await api.put(`/stl/${item.id}`, { isActive: !item.isActive });
    load();
  };

  // 상품별 그룹핑
  const grouped = list.reduce((acc, s) => {
    const key = `${s.productId}_${s.productName || ''}`;
    if (!acc[key]) acc[key] = { productId: s.productId, productName: s.productName, items: [] };
    acc[key].items.push(s);
    return acc;
  }, {});

  return (
    <div>
      <PageHeader icon={FileBox} title="신발 디자인 관리">
        <button
          onClick={() => setModal({})}
          className="flex items-center gap-2 bg-brand hover:bg-brand-600 text-white rounded-lg px-4 py-2 text-sm font-medium"
        >
          <Plus size={16} /> STL 등록
        </button>
      </PageHeader>

      <div className="space-y-6">
        {Object.values(grouped).map(group => (
          <div key={group.productId} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-800 bg-gray-800/50">
              <h2 className="text-white font-semibold text-sm">
                {group.productName || `상품 #${group.productId}`}
                <span className="text-gray-500 font-normal ml-2">ID: {group.productId}</span>
              </h2>
            </div>
            <div className="divide-y divide-gray-800">
              {group.items.map(s => (
                <div key={s.id} className={`flex items-center gap-4 px-5 py-3 ${!s.isActive ? 'opacity-50' : ''}`}>
                  <FileBox size={18} className="text-gray-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium">{s.name}</span>
                      <span className="text-xs bg-gray-800 text-gray-400 px-2 py-0.5 rounded">
                        {FOOT_LABEL[s.footSide]}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5 truncate">
                      {s.originalName} · {s.fileSizeBytes ? `${(s.fileSizeBytes / 1024 / 1024).toFixed(1)}MB` : ''} · {dayjs(s.createdAt).format('YYYY-MM-DD')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setPreview(s)} className="p-1.5 text-gray-500 hover:text-brand rounded-lg hover:bg-gray-800" title="3D 프리뷰">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => toggleActive(s)} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
                      {s.isActive ? <ToggleRight size={16} className="text-green-400" /> : <ToggleLeft size={16} />}
                    </button>
                    <button onClick={() => setModal(s)} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => del(s.id)} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-gray-800">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center">
            <FileBox size={40} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">등록된 STL 디자인이 없습니다.</p>
            <button onClick={() => setModal({})} className="mt-3 text-brand text-sm hover:underline">
              첫 번째 디자인 등록하기
            </button>
          </div>
        )}
      </div>

      {modal !== null && (
        <StlModal
          stl={modal?.id ? modal : null}
          onSave={() => { load(); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreview(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-white font-bold">{preview.name}</h2>
                <p className="text-gray-500 text-xs">
                  {preview.productName || `상품 #${preview.productId}`} · {FOOT_LABEL[preview.footSide] || preview.footSide}
                </p>
              </div>
              <button onClick={() => setPreview(null)} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
                <X size={18} />
              </button>
            </div>
            <StlViewer url={`/api/stl/${preview.id}/file`} height={480} />
          </div>
        </div>
      )}
    </div>
  );
}
